import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import type { AuthUser, FaqData, Favorite } from '../types';
import { tokenStorage } from '../api/apiClient';
import { getCurrentUser, fetchFavorites, addFavorite, removeFavorite } from '../api/services';

// ============================================================
// STATE SHAPE
// ============================================================

interface AppState {
  authentication: {
    currentUser: AuthUser | null;
    isAuthenticated: boolean;
    isLoading: boolean;
  };
  cache: {
    faqData: FaqData | null;
    favorites: Favorite[];
  };
  notifications: {
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
    isVisible: boolean;
  };
}

// ============================================================
// ACTIONS
// ============================================================

type AppAction =
  | { type: 'AUTH_LOGIN_SUCCESS'; payload: AuthUser }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_SET_LOADING'; payload: boolean }
  | { type: 'CACHE_SET_FAQ'; payload: FaqData }
  | { type: 'FAVORITES_SET'; payload: Favorite[] }
  | { type: 'FAVORITES_ADD'; payload: Favorite }
  | { type: 'FAVORITES_REMOVE'; payload: string } // documentId
  | {
      type: 'NOTIFICATION_SHOW';
      payload: { message: string; severity: AppState['notifications']['severity'] };
    }
  | { type: 'NOTIFICATION_HIDE' };

// ============================================================
// INITIAL STATE
// ============================================================

const INITIAL_STATE: AppState = {
  authentication: {
    currentUser: null,
    isAuthenticated: Boolean(tokenStorage.getToken()),
    isLoading: false,
  },
  cache: {
    faqData: null,
    favorites: [],
  },
  notifications: {
    message: '',
    severity: 'info',
    isVisible: false,
  },
};

function getFavoriteListingDocumentId(favorite: Favorite | null | undefined): string | undefined {
  const listing = favorite?.listing_transport_company;

  if (!listing) return undefined;
  if (typeof listing === 'string') return listing;
  if (typeof listing === 'object') return listing.documentId;

  return undefined;
}

function isFavoriteRecord(favorite: Favorite | null | undefined): favorite is Favorite {
  return Boolean(favorite?.documentId);
}

// ============================================================
// REDUCER
// ============================================================

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'AUTH_LOGIN_SUCCESS':
      return {
        ...state,
        authentication: {
          currentUser: action.payload,
          isAuthenticated: true,
          isLoading: false,
        },
      };

    case 'AUTH_LOGOUT':
      tokenStorage.removeToken();
      return {
        ...state,
        authentication: {
          currentUser: null,
          isAuthenticated: false,
          isLoading: false,
        },
      };

    case 'AUTH_SET_LOADING':
      return {
        ...state,
        authentication: { ...state.authentication, isLoading: action.payload },
      };

    case 'CACHE_SET_FAQ':
      return {
        ...state,
        cache: { ...state.cache, faqData: action.payload },
      };

    case 'FAVORITES_SET':
      return {
        ...state,
        cache: { ...state.cache, favorites: action.payload.filter(isFavoriteRecord) },
      };

    case 'FAVORITES_ADD':
      if (!isFavoriteRecord(action.payload)) {
        return state;
      }

      return {
        ...state,
        cache: {
          ...state.cache,
          favorites: [
            ...state.cache.favorites.filter(
              (fav) => fav.documentId !== action.payload.documentId
            ),
            action.payload,
          ],
        },
      };

    case 'FAVORITES_REMOVE':
      return {
        ...state,
        cache: {
          ...state.cache,
          favorites: state.cache.favorites.filter(
            (fav) => fav.documentId !== action.payload
          ),
        },
      };

    case 'NOTIFICATION_SHOW':
      return {
        ...state,
        notifications: {
          message: action.payload.message,
          severity: action.payload.severity,
          isVisible: true,
        },
      };

    case 'NOTIFICATION_HIDE':
      return {
        ...state,
        notifications: { ...state.notifications, isVisible: false },
      };

    default:
      return state;
  }
}

// ============================================================
// CONTEXT
// ============================================================

interface AppContextValue {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  loginUser: (user: AuthUser, token: string) => void;
  logoutUser: () => void;
  refreshUser: () => Promise<void>;
  showNotification: (
    message: string,
    severity?: AppState['notifications']['severity']
  ) => void;
  hideNotification: () => void;
  cacheFaqData: (data: FaqData) => void;
  toggleFavorite: (listingDocumentId: string) => Promise<void>;
  refreshFavorites: () => Promise<void>;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

// ============================================================
// PROVIDER
// ============================================================

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, INITIAL_STATE);
  const fullUserRefreshes = React.useRef(new Set<number>());

  const loginUser = useCallback((user: AuthUser, token: string) => {
    tokenStorage.setToken(token);
    dispatch({ type: 'AUTH_LOGIN_SUCCESS', payload: user });

    if (fullUserRefreshes.current.has(user.id)) return;

    fullUserRefreshes.current.add(user.id);
    dispatch({ type: 'AUTH_SET_LOADING', payload: true });

    getCurrentUser()
      .then((fullUser) => {
        dispatch({ type: 'AUTH_LOGIN_SUCCESS', payload: fullUser });
      })
      .catch((error) => {
        console.error('[AppProvider] Failed to load full user after login:', error);
        dispatch({ type: 'AUTH_SET_LOADING', payload: false });
      });
  }, []);

  const logoutUser = useCallback(() => {
    fullUserRefreshes.current.clear();
    dispatch({ type: 'AUTH_LOGOUT' });
  }, []);

  const showNotification = useCallback(
    (
      message: string,
      severity: AppState['notifications']['severity'] = 'info'
    ) => {
      dispatch({ type: 'NOTIFICATION_SHOW', payload: { message, severity } });
    },
    []
  );

  const refreshUser = useCallback(async () => {
    dispatch({ type: 'AUTH_SET_LOADING', payload: true });
    try {
      const user = await getCurrentUser();
      dispatch({ type: 'AUTH_LOGIN_SUCCESS', payload: user });
    } catch (error) {
      console.error('[AppProvider] Failed to refresh user:', error);
      dispatch({ type: 'AUTH_SET_LOADING', payload: false });
      // If refresh fails, we might want to logout if it's an auth error
    }
  }, []);

  const refreshFavorites = useCallback(async () => {
    try {
      const response = await fetchFavorites();
      dispatch({ type: 'FAVORITES_SET', payload: response.data });
    } catch (error) {
      console.error('[AppProvider] Failed to fetch favorites:', error);
    }
  }, []);

  const toggleFavorite = useCallback(
    async (listingDocumentId: string) => {
      const existing = state.cache.favorites.find(
        (fav) => getFavoriteListingDocumentId(fav) === listingDocumentId
      );

      if (existing) {
        // Optimistic update (remove)
        dispatch({ type: 'FAVORITES_REMOVE', payload: existing.documentId });
        try {
          await removeFavorite(listingDocumentId);
        } catch (error) {
          console.error('[AppProvider] Failed to remove favorite:', error);
          // Rollback
          dispatch({ type: 'FAVORITES_ADD', payload: existing });
          showNotification('Nie udało się usunąć z zapisanych.', 'error');
        }
      } else {
        // Optimistic update (add)
        // Note: we use a temp id for the optimistic state
        const tempId = `temp-${Date.now()}`;
        const tempFavorite: Favorite = {
          id: -1,
          documentId: tempId,
          listing_transport_company: {
            documentId: listingDocumentId,
          } as any,
        };
        dispatch({ type: 'FAVORITES_ADD', payload: tempFavorite });

        try {
          const response = await addFavorite(listingDocumentId);
          // Replace temp favorite with the real one
          dispatch({ type: 'FAVORITES_REMOVE', payload: tempId });
          if (isFavoriteRecord(response.data)) {
            dispatch({ type: 'FAVORITES_ADD', payload: response.data });
          } else {
            await refreshFavorites();
          }
        } catch (error) {
          console.error('[AppProvider] Failed to add favorite:', error);
          // Rollback
          dispatch({ type: 'FAVORITES_REMOVE', payload: tempId });
          showNotification('Nie udało się zapisać ogłoszenia.', 'error');
        }
      }
    },
    [state.cache.favorites, refreshFavorites, showNotification]
  );

  useEffect(() => {
    if (state.authentication.isAuthenticated && !state.authentication.currentUser) {
      refreshUser();
    }
  }, [state.authentication.isAuthenticated, state.authentication.currentUser, refreshUser]);

  useEffect(() => {
    if (state.authentication.isAuthenticated && state.authentication.currentUser) {
      refreshFavorites();
    }
  }, [state.authentication.isAuthenticated, !!state.authentication.currentUser, refreshFavorites]);


  const hideNotification = useCallback(() => {
    dispatch({ type: 'NOTIFICATION_HIDE' });
  }, []);

  const cacheFaqData = useCallback((data: FaqData) => {
    dispatch({ type: 'CACHE_SET_FAQ', payload: data });
  }, []);

  const contextValue: AppContextValue = {
    state,
    dispatch,
    loginUser,
    logoutUser,
    refreshUser,
    showNotification,
    hideNotification,
    cacheFaqData,
    toggleFavorite,
    refreshFavorites,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
}

// ============================================================
// HOOKS
// ============================================================

export function useAppContext(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
}

export function useAuthentication() {
  const { state, loginUser, logoutUser, refreshUser } = useAppContext();
  return {
    ...state.authentication,
    loginUser,
    logoutUser,
    refreshUser,
  };
}

export function useNotifications() {
  const { state, showNotification, hideNotification } = useAppContext();
  return {
    ...state.notifications,
    showNotification,
    hideNotification,
  };
}

export function useFaqCache() {
  const { state, cacheFaqData } = useAppContext();
  return {
    cachedFaqData: state.cache.faqData,
    cacheFaqData,
  };
}

export function useFavorites() {
  const { state, toggleFavorite, refreshFavorites } = useAppContext();
  return {
    favorites: state.cache.favorites,
    toggleFavorite,
    refreshFavorites,
    isFavorite: (listingDocumentId: string) =>
      state.cache.favorites.some(
        (fav) => getFavoriteListingDocumentId(fav) === listingDocumentId
      ),
  };
}
